import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { usePatchBroadcastScheduleProgramSegment } from '@/features/broadcast-schedule-program/api/create-broadcast-schedule-program-segment';
import { useDeleteBroadcastScheduleProgramSegment } from '@/features/broadcast-schedule-program/api/delete-broadcast-schedule-program-segment';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { TSegment } from '@/features/segments/types';
import { formatInAppTz, secondsOfDayInAppTz } from '@/utils/spain-time';

export function useBroadcastScheduleProgramBodySegmentDetailCardBodySegmentListState({
  items: segments,
  setSegmentItems,
}: {
  items: TSegment[];
  setSegmentItems: Dispatch<SetStateAction<TSegment[]>>;
}) {
  const { id = '' } = useParams();
  const {
    detailCardRef,
    activeSegment,
    playerVideoCurrentTime,
    getDateTimeCurrentPresence,
    setPendingSeekTime,
    setActiveSegment,
    setPlayerVideoCurrentTime,
    setTotalSecondsItems,
  } = useBroadcastScheduleProgram();
  const { mutateAsync: createSegment } = usePatchBroadcastScheduleProgramSegment();
  const { mutateAsync: deleteSegment } = useDeleteBroadcastScheduleProgramSegment();

  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [visibleStartIndex, setVisibleStartIndex] = useState(0);
  const [width, setWidth] = useState(0);
  const [disableAddPreviousSegment, setDisableAddPreviousSegment] = useState(false);
  const [disableAddNextSegment, setDisableAddNextSegment] = useState(false);
  const [sliderValue, setSliderValue] = useState(0);

  useEffect(() => {
    if (activeSegment) {
      const index = segments.findIndex(seg => seg.id === activeSegment.id);
      setSelectedIndex(index);

      const centerOffset = Math.floor(visibleItemsCount / 2);
      const newStartIndex = Math.min(
        Math.max(index - centerOffset, 0),
        Math.max(segments.length - visibleItemsCount, 0)
      );
      setVisibleStartIndex(newStartIndex);
    }
  }, [activeSegment, segments]);

  useEffect(() => {
    const observer = new ResizeObserver(entries => {
      if (entries[0]) {
        setWidth(entries[0].contentRect.width);
      }
    });

    if (detailCardRef.current) {
      observer.observe(detailCardRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const visibleItemsCount = useMemo(() => {
    if (!detailCardRef?.current) {
      return 10;
    }
    if (width < 480) {
      return 1;
    }
    if (width < 640) {
      return 4;
    }
    if (width < 768) {
      return 6;
    }
    if (width < 1024) {
      return 8;
    }
    if (width < 1280) {
      return 12;
    }
    return 15;
  }, [width]);

  const maxStartIndex = useMemo(
    () => Math.max(0, segments.length - visibleItemsCount),
    [segments.length, visibleItemsCount]
  );

  const handleSliderChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(event.target.value);
      const newStartIndex = Math.round(newValue * maxStartIndex);
      setVisibleStartIndex(newStartIndex);
    },
    [maxStartIndex]
  );

  const handleWheel = useCallback(
    (event: React.WheelEvent<HTMLInputElement>) => {
      event.preventDefault();

      const deltaFactor = 0.025;
      const delta = event.deltaY > 0 ? -deltaFactor : deltaFactor;

      let newValue = sliderValue + delta;
      newValue = Math.min(1, Math.max(0, newValue));

      setSliderValue(newValue);

      const newStartIndex = Math.round(newValue * maxStartIndex);
      setVisibleStartIndex(newStartIndex);
    },
    [sliderValue, maxStartIndex]
  );

  const handleAddSegment = async (where: 'previous' | 'next') => {
    try {
      const item = await createSegment({ action: where, id });
      if (!item) {
        if (where === 'previous') {
          setDisableAddPreviousSegment(true);
        } else if (where === 'next') {
          setDisableAddNextSegment(true);
        }
        return;
      }
      const newSegment: TSegment = {
        ...item,
        animation: where === 'previous' ? 'enter-left' : 'enter-right',
      };

      setSegmentItems(prev =>
        where === 'previous' ? [newSegment, ...prev] : [...prev, newSegment]
      );
    } catch (error) {
      console.error(error);
    }
  };

  const handleRemoveSegment = (where: 'first' | 'last') => {
    if (segments.length === 0) return;

    const removeIndex = where === 'first' ? 0 : segments.length - 1;
    const animation = where === 'first' ? 'exit-left' : 'exit-right';

    try {
      deleteSegment({ id, action: where }).then(() => {
        setSegmentItems(prev =>
          prev.map((seg, index) => (index === removeIndex ? { ...seg, animation } : seg))
        );
        setTimeout(() => {
          setSegmentItems(prev => prev.filter((_, index) => index !== removeIndex));

          if (activeSegment?.id === segments[removeIndex].id) {
            const newActiveSegment =
              segments.length === 1 ? null : segments[removeIndex === 0 ? 1 : removeIndex - 1];
            setActiveSegment(newActiveSegment);
          }
        }, 300);

        if (where === 'first') {
          setDisableAddPreviousSegment(false);
        } else if (where === 'last') {
          setDisableAddNextSegment(false);
        }
      });
    } catch (error) {
      console.error(error);
    }
  };

  const visibleItems = useMemo(() => {
    return segments.slice(visibleStartIndex, visibleStartIndex + visibleItemsCount);
  }, [segments, visibleStartIndex, visibleItemsCount]);

  const totalSecondsItems = useMemo(() => {
    const totalSeconds = segments.reduce((acc, item) => acc + item.duration_in_seconds, 0);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [segments]);

  useEffect(() => {
    const totalSeconds = segments.reduce((acc, item) => acc + item.duration_in_seconds, 0);
    setTotalSecondsItems(totalSeconds);
  }, [segments, setTotalSecondsItems]);

  const currentSecondGlobal = useMemo(() => {
    if (!activeSegment || !segments.length) return '00:00';
    const previousSeconds = segments
      .slice(
        0,
        segments.findIndex(item => item.id === activeSegment?.id)
      )
      .reduce((acc, item) => acc + item.duration_in_seconds, 0);
    const globalSeconds = previousSeconds + Math.floor(playerVideoCurrentTime);
    const minutes = Math.floor(globalSeconds / 60);
    const seconds = globalSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }, [Math.floor(playerVideoCurrentTime), activeSegment, segments]);

  /**
   * ============================
   *  Hora absoluta (HH:mm:ss)
   * ============================
   *
   * Read and typed as the Madrid wall clock (RCOMM-6264), whatever the viewer's
   * timezone: an analyst in Caracas jumps to 10:00:00 as it aired in Spain.
   */

  const earliestTime = useMemo(() => {
    if (!segments.length) return '00:00:00';
    const earliestSeg = segments.reduce((min, seg) =>
      new Date(seg.start_datetime) < new Date(min.start_datetime) ? seg : min
    );
    return formatInAppTz(earliestSeg.start_datetime, 'HH:mm:ss');
  }, [segments]);

  const latestTime = useMemo(() => {
    if (!segments.length) return '23:59:59';
    const latestSeg = segments.reduce((max, seg) =>
      new Date(seg.end_datetime) > new Date(max.end_datetime) ? seg : max
    );
    return formatInAppTz(latestSeg.end_datetime, 'HH:mm:ss');
  }, [segments]);

  const currentAbsoluteTime = useMemo(() => {
    if (!activeSegment) return earliestTime;
    // Anchor on the SAME base that gets persisted (getDateTimeCurrentPresence):
    // the clip start while timekeeping (clip video, clip-relative playerVideoCurrentTime)
    // and the segment start in cortes mode. Anchoring unconditionally on the segment
    // start mismatched the stored presence time by clip.start - segment.start
    // (up to minutes) during timekeeping (RCOMM-5972).
    // getAbsoluteDateFromPlayerTime already rounds to a whole second, so this shown
    // value is identical to what gets persisted; the round() below is a defensive
    // no-op in case the anchor ever returns a sub-second Date.
    const absDate = getDateTimeCurrentPresence(playerVideoCurrentTime);
    if (!absDate) return earliestTime;
    const rounded = new Date(Math.round(absDate.getTime() / 1000) * 1000);
    return formatInAppTz(rounded, 'HH:mm:ss');
  }, [activeSegment, playerVideoCurrentTime, earliestTime, getDateTimeCurrentPresence]);

  const handleChangeAbsoluteTimeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const regex = /^(\d{1,2}):(\d{1,2}):(\d{1,2})$/;
    const match = value.match(regex);
    if (!match) return;

    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);
    if (hours > 23 || minutes > 59 || seconds > 59) return;

    const targetSeconds = hours * 3600 + minutes * 60 + seconds;

    if (!segments.length) return;

    const earliestSeconds = secondsOfDayInAppTz(segments[0].start_datetime);
    const latestSeconds = secondsOfDayInAppTz(segments[segments.length - 1].end_datetime);

    if (targetSeconds < earliestSeconds || targetSeconds > latestSeconds) return;

    let newActiveSegment: TSegment | null = null;
    let newPlayerTime = 0;

    for (let i = 0; i < segments.length; i++) {
      const seg = segments[i];
      const segStartSec = secondsOfDayInAppTz(seg.start_datetime);
      const segEndSec = secondsOfDayInAppTz(seg.end_datetime);
      if (targetSeconds >= segStartSec && targetSeconds < segEndSec) {
        newActiveSegment = seg;
        newPlayerTime = targetSeconds - segStartSec;
        break;
      }
    }

    if (!newActiveSegment) {
      // Si no se encuentra, asumir último
      newActiveSegment = segments[segments.length - 1];
      const segStartSec = secondsOfDayInAppTz(newActiveSegment.start_datetime);
      newPlayerTime = Math.max(0, targetSeconds - segStartSec);
    }

    setActiveSegment({ ...newActiveSegment });
    setPlayerVideoCurrentTime(newPlayerTime);
    setPendingSeekTime(newPlayerTime);
  };

  const handleChangeTimeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    const regex = /^(\d{1,3}):([0-5]?\d)$/; // hasta 999 minutos, segundos válidos
    const match = value.match(regex);
    if (!match) return;

    const minutes = parseInt(match[1]);
    const seconds = parseInt(match[2]);

    const newGlobalSeconds = minutes * 60 + seconds;

    let segmentStart = 0;
    let newActiveSegment: TSegment | null = null;
    let newPlayerTime = 0;

    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const segmentEnd = segmentStart + segment.duration_in_seconds;
      if (newGlobalSeconds >= segmentStart && newGlobalSeconds < segmentEnd) {
        newActiveSegment = segment;
        newPlayerTime = newGlobalSeconds - segmentStart;
        break;
      }
      segmentStart = segmentEnd;
    }

    if (newActiveSegment) {
      setActiveSegment({ ...newActiveSegment });
      setPlayerVideoCurrentTime(newPlayerTime);
      setPendingSeekTime(newPlayerTime);
    } else if (segments.length > 0) {
      const lastSegment = segments[segments.length - 1];
      const totalStart = segments
        .slice(0, segments.length - 1)
        .reduce((acc, item) => acc + item.duration_in_seconds, 0);
      newPlayerTime = newGlobalSeconds - totalStart;
      setActiveSegment({ ...lastSegment });
      setPlayerVideoCurrentTime(newPlayerTime);
      setPendingSeekTime(newPlayerTime);
    }
  };

  const denominator = segments.length - visibleItemsCount;

  useEffect(() => {
    setSliderValue(denominator ? visibleStartIndex / denominator : 0);
  }, [visibleStartIndex, denominator]);

  return {
    segments,
    visibleItems,
    totalSecondsItems,
    currentSecondGlobal,
    currentAbsoluteTime,
    earliestTime,
    latestTime,
    sliderValue,
    detailCardRef,
    visibleStartIndex,
    selectedIndex,
    disableAddPreviousSegment,
    disableAddNextSegment,
    handleAddSegment,
    handleRemoveSegment,
    handleSliderChange,
    handleChangeTimeInput,
    handleChangeAbsoluteTimeInput,
    handleWheel,
  };
}
